<template>
  <div dir="rtl" class="min-h-screen bg-base text-white font-sans pb-24">
    <div class="max-w-7xl mx-auto px-4 pt-6">
      
      <!-- Header -->
      <header class="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6 md:mb-8">
        <div class="space-y-1 w-full md:w-auto">
          <div class="flex items-center justify-between">
            <h1 class="text-xl md:text-2xl font-extrabold text-white flex items-center gap-2">
              <span class="p-1.5 md:p-2 rounded-xl bg-[#b9f1cf]/10 text-[#b9f1cf] shadow-[0_0_15px_rgba(185,241,207,0.15)]">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 md:h-6 md:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              </span>
              دارایی‌های من
            </h1>
            
            <!-- Mobile Refresh Button (Only visible on mobile) -->
            <button @click="fetchAssets" class="md:hidden p-2 rounded-xl bg-white/5 ring-1 ring-white/10 hover:bg-white/10 text-white/70 active:scale-95 transition">
               <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>

          <div class="flex flex-wrap items-center gap-2 md:gap-3">
            <p class="text-white/50 text-xs md:text-sm hidden md:block">موجودی کیف پول و معاملات آنی</p>
            
            <!-- Status Pill -->
            <div class="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#141a1d] border border-white/5 transition-all duration-300" :class="connected ? 'border-emerald-500/30' : 'border-rose-500/30'">
              <span class="relative flex h-1.5 w-1.5 md:h-2 md:w-2">
                <span v-if="connected" class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span class="relative inline-flex rounded-full h-1.5 w-1.5 md:h-2 md:w-2" :class="connected ? 'bg-emerald-500' : 'bg-rose-500'"></span>
              </span>
              <span class="text-[9px] md:text-[10px] font-medium" :class="connected ? 'text-emerald-400' : 'text-rose-400'">
                {{ connected ? 'متصل' : 'قطع' }}
              </span>
            </div>

            <!-- Role Pills -->
            <div v-if="userRole === 'COWORKER'" class="px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-[9px] md:text-[10px] text-amber-400 font-bold">
              همکار
            </div>

            <div v-if="userRole === 'ADMIN'" class="flex items-center gap-1 bg-[#000]/30 p-0.5 rounded-lg ring-1 ring-white/10">
              <button @click="adminViewMode = 'USER'" class="px-2 py-0.5 text-[9px] rounded transition-all" :class="adminViewMode === 'USER' ? 'bg-[#b9f1cf] text-[#0a1f14] font-bold' : 'text-white/50'">کاربر</button>
              <button @click="adminViewMode = 'COWORKER'" class="px-2 py-0.5 text-[9px] rounded transition-all" :class="adminViewMode === 'COWORKER' ? 'bg-amber-400 text-[#0a1f14] font-bold' : 'text-white/50'">همکار</button>
            </div>
          </div>
        </div>
        
        <!-- Desktop Refresh Button -->
        <button @click="fetchAssets" class="hidden md:flex px-4 py-2 rounded-xl bg-white/5 ring-1 ring-white/10 hover:bg-white/10 transition items-center gap-2 text-sm group">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 group-hover:rotate-180 transition-transform duration-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          بروزرسانی
        </button>
      </header>

      <!-- Loading State -->
      <div v-if="loading" class="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
         <div v-for="i in 6" :key="i" class="h-32 md:h-48 rounded-2xl md:rounded-3xl bg-[#141a1d] animate-pulse ring-1 ring-white/5"></div>
      </div>

      <!-- Content -->
      <div v-else>
        <!-- Empty State -->
        <div v-if="assets.length === 0" class="text-center py-12 md:py-20 bg-[#141a1d]/50 rounded-3xl border border-dashed border-white/10">
          <p class="text-white/60 text-sm">هیچ دارایی یافت نشد.</p>
        </div>

        <div v-else>
          <!-- DESKTOP/TABLET VIEW (Table) -->
          <div class="hidden md:block rounded-2xl bg-[#141a1d]/90 backdrop-blur-xl ring-1 ring-[#334D44]/40 shadow-xl overflow-hidden">
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-white/5">
                <thead>
                  <tr class="bg-white/5">
                    <th class="px-6 py-4 text-right text-xs font-bold text-[#b9f1cf]">عنوان دارایی</th>
                    <th class="px-6 py-4 text-center text-xs font-bold text-white/50">واحد</th>
                    <th class="px-6 py-4 text-center text-xs font-bold text-white/50">موجودی</th>
                    <th class="px-6 py-4 text-center text-xs font-bold text-white/50">قیمت تابلو (تومان)</th>
                    <th class="px-6 py-4 text-center text-xs font-bold text-white/50">عملیات</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-white/5">
                  <tr v-for="asset in processedAssets" :key="asset.name" class="group hover:bg-white/[0.02]" :class="asset.balance > 0 ? '' : 'opacity-80 grayscale-[0.3]'">
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div class="flex items-center">
                        <div class="shrink-0 w-1.5 h-8 rounded-full transition-colors" :class="asset.balance > 0 ? 'bg-[#b9f1cf]' : 'bg-white/10'"></div>
                        <div class="mr-4">
                          <div class="text-sm font-bold text-white">{{ asset.persianName }}</div>
                          <div class="text-[10px] text-white/40 font-mono mt-0.5">{{ asset.name }}</div>
                        </div>
                      </div>
                    </td>
                    <td class="px-6 py-4 text-center"><span class="bg-white/5 text-white/70 px-2 py-0.5 rounded text-xs">{{ asset.unit }}</span></td>
                    <td class="px-6 py-4 text-center">
                        <span class="text-lg font-mono font-bold">{{ formatNumber(asset.balance) }}</span>
                    </td>
                    <td class="px-6 py-4 text-center">
                      <div v-if="asset.marketData" class="flex flex-col items-center gap-1">
                        <div class="text-xs"><span class="text-white/40">خ:</span> <span class="text-emerald-400 font-mono">{{ formatNumber(asset.marketData.buy) }}</span></div>
                        <div class="text-xs"><span class="text-white/40">ف:</span> <span class="text-rose-400 font-mono">{{ formatNumber(asset.marketData.sell) }}</span></div>
                      </div>
                      <div v-else class="text-xs text-white/20">...</div>
                    </td>
                    <td class="px-6 py-4 text-center">
                      <button 
                        v-if="connected && asset.marketData"
                        @click="openTradeModal(asset)"
                        :disabled="!asset.marketData.buyActive && !asset.marketData.sellActive"
                        class="px-4 py-2 rounded-xl text-xs font-extrabold transition-all shadow-lg hover:scale-105 active:scale-95"
                        :class="(!asset.marketData.buyActive && !asset.marketData.sellActive) ? 'bg-white/10 text-white/30 cursor-not-allowed' : 'bg-[#b9f1cf] text-[#0a1f14] hover:bg-[#a0ecc0]'"
                      >
                        <span v-if="!asset.marketData.buyActive && !asset.marketData.sellActive">بسته</span>
                        <span v-else>معامله</span>
                      </button>
                      <span v-else class="text-[10px] text-white/30">غیرقابل معامله</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- MOBILE VIEW (Compact Grid) -->
          <div class="md:hidden grid grid-cols-2 gap-2.5">
            <div 
              v-for="asset in processedAssets" 
              :key="asset.name"
              class="bg-[#141a1d] rounded-2xl p-3 ring-1 ring-white/5 relative overflow-hidden flex flex-col justify-between min-h-[140px]"
              :class="asset.balance > 0 ? 'ring-[#b9f1cf]/20' : ''"
            >
              <!-- Top: Name -->
              <div>
                <div class="flex items-center justify-between mb-2">
                  <div class="text-[11px] font-bold text-white truncate max-w-[80%] leading-tight">
                    {{ asset.persianName }}
                  </div>
                  <div class="w-1.5 h-1.5 rounded-full" :class="asset.balance > 0 ? 'bg-[#b9f1cf] shadow-[0_0_5px_#b9f1cf]' : 'bg-white/10'"></div>
                </div>
                
                <!-- Middle: Balance -->
                <div class="text-center py-1">
                  <div class="text-xl font-extrabold font-mono tracking-tighter" :class="asset.balance > 0 ? 'text-white' : 'text-white/20'">
                    {{ formatNumber(asset.balance) }}
                  </div>
                  <div class="text-[9px] text-white/30 -mt-1">{{ asset.unit }}</div>
                </div>
              </div>

              <!-- Bottom: Price & Action -->
              <div class="mt-2 space-y-2">
                <!-- Prices -->
                <div v-if="asset.marketData" class="flex justify-between text-[9px] bg-white/5 rounded-lg px-1.5 py-1">
                  <div class="flex flex-col items-start">
                    <span class="text-white/30">خرید</span>
                    <span class="text-emerald-400 font-mono">{{ formatNumber(asset.marketData.buy) }}</span>
                  </div>
                  <div class="h-full w-px bg-white/10 mx-1"></div>
                  <div class="flex flex-col items-end">
                    <span class="text-white/30">فروش</span>
                    <span class="text-rose-400 font-mono">{{ formatNumber(asset.marketData.sell) }}</span>
                  </div>
                </div>
                <div v-else class="text-center text-[9px] text-white/20 py-1">در حال دریافت...</div>

                <!-- Button -->
                <button 
                  v-if="connected && asset.marketData"
                  @click="openTradeModal(asset)"
                  :disabled="!asset.marketData.buyActive && !asset.marketData.sellActive"
                  class="w-full py-2 rounded-lg text-[10px] font-bold transition-all active:scale-95 flex items-center justify-center"
                  :class="(!asset.marketData.buyActive && !asset.marketData.sellActive) 
                    ? 'bg-white/5 text-white/30 cursor-not-allowed' 
                    : 'bg-[#b9f1cf] text-[#0a1f14] hover:bg-[#a0ecc0] shadow-[0_0_10px_-2px_rgba(185,241,207,0.3)]'"
                >
                  {{ (!asset.marketData.buyActive && !asset.marketData.sellActive) ? 'بسته' : 'معامله' }}
                </button>
                <div v-else class="w-full py-1.5 text-center text-[9px] text-white/20 bg-white/5 rounded-lg">
                  ---
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Trade Modal -->
      <transition name="modal">
        <div
          v-if="walletBuyModal"
          class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
          @click.self="closeWalletBuy"
        >
          <div class="relative w-full max-w-sm rounded-[2rem] overflow-hidden bg-[#0f1417] border border-white/10 shadow-[0_0_60px_-10px_rgba(0,0,0,0.8)] transform transition-all">
            <!-- Modal Header -->
            <div class="flex items-center justify-between px-6 py-5 border-b border-white/5 bg-[#141a1e]">
              <div>
                <h2 class="text-sm font-extrabold text-white tracking-wide">
                  {{ walletBuyItem?.persianName }}
                </h2>
                <div class="text-[10px] text-white/40 mt-0.5 font-mono">{{ walletBuyItem?.name }}</div>
              </div>
              <button @click="closeWalletBuy" class="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-white/60 hover:bg-white/10 hover:text-white transition">✕</button>
            </div>

            <div class="p-6 space-y-6">
              <!-- Buy/Sell Tabs -->
              <div class="grid grid-cols-2 gap-1 bg-[#0a0e10] p-1.5 rounded-2xl border border-white/10">
                <button 
                  @click="walletBuyType = 'buy'"
                  :disabled="!walletBuyItem?.marketData?.buyActive"
                  class="py-3 rounded-xl text-xs font-bold transition-all flex flex-col items-center gap-1 relative overflow-hidden"
                  :class="[
                    walletBuyType === 'buy' ? 'bg-[#b9f1cf] text-[#0a1f14] shadow-lg' : 'text-white/40 hover:text-white hover:bg-white/5',
                    !walletBuyItem?.marketData?.buyActive ? 'opacity-30 cursor-not-allowed' : ''
                  ]"
                >
                  <span>خرید از ما</span>
                  <span class="text-[9px] font-mono mt-0.5 text-emerald-600/80 font-bold" v-if="walletBuyItem?.marketData?.buyActive">
                    {{ formatNumber(walletBuyItem?.marketData?.sell) }}
                  </span>
                </button>
                <button 
                  @click="walletBuyType = 'sell'"
                  :disabled="!walletBuyItem?.marketData?.sellActive"
                  class="py-3 rounded-xl text-xs font-bold transition-all flex flex-col items-center gap-1 relative overflow-hidden"
                  :class="[
                    walletBuyType === 'sell' ? 'bg-rose-500 text-white shadow-lg' : 'text-white/40 hover:text-white hover:bg-white/5',
                    !walletBuyItem?.marketData?.sellActive ? 'opacity-30 cursor-not-allowed' : ''
                  ]"
                >
                  <span>فروش به ما</span>
                  <span class="text-[9px] font-mono mt-0.5 text-rose-200 font-bold" v-if="walletBuyItem?.marketData?.sellActive">
                    {{ formatNumber(walletBuyItem?.marketData?.buy) }}
                  </span>
                </button>
              </div>

              <!-- Input -->
              <div class="space-y-2">
                <div class="flex justify-between items-center text-[11px] font-bold text-white/70">
                  <label>مقدار / تعداد</label>
                  <span v-if="walletBuyType === 'sell'" class="text-white/40 cursor-pointer hover:text-[#b9f1cf]" @click="walletBuyAmount = walletBuyItem?.balance">
                    موجودی شما: {{ formatNumber(walletBuyItem?.balance) }}
                  </span>
                </div>
                <div class="relative group">
                  <input
                    v-model.number="walletBuyAmount"
                    type="number"
                    min="0"
                    step="0.001"
                    class="w-full rounded-2xl bg-[#0a0e10] border border-white/10 focus:border-[#b9f1cf] focus:ring-4 focus:ring-[#b9f1cf]/10 outline-none px-4 py-4 text-center text-lg font-bold text-white placeholder-white/20 transition dir-ltr"
                  />
                  <div class="absolute left-4 top-1/2 -translate-y-1/2 text-xs text-white/30 pointer-events-none">{{ walletBuyItem?.unit }}</div>
                </div>
              </div>

              <!-- Calculation -->
              <div class="rounded-2xl bg-[#141a1e] border border-white/5 p-4 flex items-center justify-between">
                <span class="text-xs text-white/60">مبلغ نهایی تراکنش</span>
                <div class="text-right">
                  <div class="text-base font-bold text-white">
                    {{ formatNumber(calculateTotal) }}
                  </div>
                  <div class="text-[10px] text-white/30">تومان</div>
                </div>
              </div>

              <!-- Submit Button -->
              <button
                @click="submitWalletBuy"
                :disabled="isSubmitting || calculateTotal <= 0"
                class="w-full py-4 rounded-2xl font-bold text-sm active:scale-[.98] transition-all shadow-xl flex items-center justify-center gap-2 relative overflow-hidden"
                :class="[
                  isSubmitting || calculateTotal <= 0 ? 'opacity-70 cursor-not-allowed bg-gray-700 text-white/30' : '',
                  !isSubmitting && calculateTotal > 0 && walletBuyType === 'buy' ? 'bg-[#b9f1cf] text-[#0a1f14] hover:bg-[#96e8b6] shadow-emerald-400/20' : '',
                  !isSubmitting && calculateTotal > 0 && walletBuyType === 'sell' ? 'bg-rose-500 text-white hover:bg-rose-600 shadow-rose-500/20' : ''
                ]"
              >
                <span v-if="isSubmitting" class="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full"></span>
                <span v-else>
                  {{ walletBuyType === 'buy' ? 'پرداخت و خرید' : 'تایید فروش' }}
                </span>
              </button>
            </div>
          </div>
        </div>
      </transition>

    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, onBeforeUnmount } from 'vue'
import axios from 'axios'
import { io } from 'socket.io-client'
import Swal from 'sweetalert2'

const API_URL = process.env.VUE_APP_API_URL || ''
const WS_URL = process.env.VUE_APP_API_WS || '' 

const assets = ref([])
const loading = ref(true)
const connected = ref(false)
const userRole = ref('USER')
const adminViewMode = ref('USER')
const rawSocketData = ref({ user: {}, coworker: {} })
let socket = null

const walletBuyModal = ref(false)
const walletBuyItem = ref(null)
const walletBuyAmount = ref(1)
const walletBuyType = ref("buy")
const isSubmitting = ref(false)

const assetDefinitions = {
  'SekehRob': { label: 'سکه ربع بهار آزادی', unit: 'عدد' },
  'SekehNim': { label: 'نیم سکه بهار آزادی', unit: 'عدد' },
  'SekehTamam': { label: 'سکه تمام بهار آزادی', unit: 'عدد' },
  'SekehEmam': { label: 'سکه امامی', unit: 'عدد' },
  'YekGram18': { label: 'یک گرم طلای ۱۸ عیار', unit: 'گرم' },
  'mazane': { label: 'مظنه (مثقال)', unit: 'مثقال' },
  'SekehGerami': { label: 'سکه گرمی', unit: 'عدد' },
  'SekehTamamGheirBanki': { label: 'سکه تمام (غیر بانکی)', unit: 'عدد' },
  'SekehNimGheirBanki': { label: 'نیم سکه (غیر بانکی)', unit: 'عدد' },
  'SekehRobGheirBanki': { label: 'ربع سکه (غیر بانکی)', unit: 'عدد' }
}

function normalizeKey(key) {
  const mapping = {
    'RobBanki': 'SekehRob',
    'NimBanki': 'SekehNim',
    'TamamBanki': 'SekehTamam',
    'ImamBanki': 'SekehEmam',
    'RobNonBanki': 'SekehRobGheirBanki',
    'NimNonBanki': 'SekehNimGheirBanki',
    'TamamNonBanki': 'SekehTamamGheirBanki',
    'Gerami': 'SekehGerami',
    'gold18': 'YekGram18'
  }
  return mapping[key] || key
}

function mapSocketKeyToStandard(key) {
  return normalizeKey(key)
}

function getSocketKeyFromStandard(walletKey) {
    const mapReverse = {
        "SekehEmam": "ImamBanki",
        "SekehTamam": "TamamBanki",
        "SekehNim": "NimBanki",
        "SekehRob": "RobBanki",
        "SekehGerami": "Gerami",
        "SekehTamamGheirBanki": "TamamNonBanki",
        "SekehNimGheirBanki": "NimNonBanki",
        "SekehRobGheirBanki": "RobNonBanki",
        "YekGram18": "gold18",
        "mazane": "mazane"
    }
    return mapReverse[walletKey] || walletKey
}

onMounted(async () => {
  await Promise.all([fetchAssets(), fetchProfile()])
  setupSocket()
})

onBeforeUnmount(() => {
  if (socket) socket.disconnect()
})

async function fetchProfile() {
  try {
    const res = await axios.get(`${API_URL}/user/profile`, { withCredentials: true })
    userRole.value = res.data.user.role || 'USER'
    if (userRole.value === 'ADMIN') {
        adminViewMode.value = 'USER'
    }
  } catch {
    userRole.value = 'USER'
  }
}

async function fetchAssets() {
  loading.value = true
  try {
    const res = await axios.get(`${API_URL}/user/wallet/assets`, { withCredentials: true })
    const rawData = res.data.data || res.data
    assets.value = Array.isArray(rawData) ? rawData : [rawData]
  } catch (err) {
    console.error(err)
  } finally {
    loading.value = false
  }
}

function setupSocket() {
    try {
        socket = io(WS_URL, { transports: ['websocket'], withCredentials: true })
        
        socket.on('connect', () => { connected.value = true })
        socket.on('disconnect', () => { connected.value = false })

        const updateHandler = (data) => {
            if (data.user) rawSocketData.value.user = data.user
            if (data.coworker) rawSocketData.value.coworker = data.coworker
        }

        socket.on('prices:init', updateHandler)
        socket.on('prices:update', updateHandler)

    } catch (err) {
        console.error("Socket Init Error:", err)
    }
}

const livePrices = computed(() => {
    let boardData;
    
    if (userRole.value === 'COWORKER') {
        boardData = rawSocketData.value.coworker;
    } else if (userRole.value === 'ADMIN') {
        boardData = adminViewMode.value === 'COWORKER' ? rawSocketData.value.coworker : rawSocketData.value.user;
    } else {
        boardData = rawSocketData.value.user;
    }
    
    if (!boardData) return {}

    const prices = {}

    if (boardData.coins) {
        Object.keys(boardData.coins).forEach(socketKey => {
            const standardKey = mapSocketKeyToStandard(socketKey)
            const coinData = boardData.coins[socketKey]
            prices[standardKey] = {
                buy: coinData.buy, 
                sell: coinData.sell, 
                buyActive: coinData.buy_active !== false,
                sellActive: coinData.sell_active !== false
            }
        })
    }
    
    if (boardData.gold18) {
        prices['YekGram18'] = {
            buy: boardData.gold18.buy,
            sell: boardData.gold18.sell,
            buyActive: boardData.gold18.buy_active !== false,
            sellActive: boardData.gold18.sell_active !== false
        }
    }
    
    if (boardData.mazane) {
         prices['mazane'] = {
            buy: boardData.mazane,
            sell: boardData.mazane, 
            buyActive: true,
            sellActive: true
        }
    }

    return prices
})

const processedAssets = computed(() => {
  const map = new Map()

  assets.value.forEach(asset => {
    if (['Dollar', 'Derham'].includes(asset.name)) return

    const normKey = normalizeKey(asset.name)
    
    if (!map.has(normKey)) {
        map.set(normKey, { 
            name: normKey, 
            balance: 0,
            originalIds: []
        })
    }
    
    const item = map.get(normKey)
    item.balance += Number(asset.balance) || 0
    item.originalIds.push(asset._id)
  })

  return Array.from(map.values()).map(asset => {
    const def = assetDefinitions[asset.name] || { label: asset.name, unit: 'واحد' }
    const marketInfo = livePrices.value[asset.name] || null
    
    return {
      ...asset,
      persianName: def.label,
      unit: def.unit,
      marketData: marketInfo
    }
  }).sort((a, b) => {
    if (a.balance > 0 && b.balance === 0) return -1
    if (a.balance === 0 && b.balance > 0) return 1
    return 0
  })
})

function formatNumber(num) {
  if (num === undefined || num === null) return '0'
  return Number(num).toLocaleString('fa-IR', { maximumFractionDigits: 4 })
}

const calculateTotal = computed(() => {
    if (!walletBuyItem.value || !walletBuyItem.value.marketData) return 0
    const price = walletBuyType.value === 'buy' 
        ? walletBuyItem.value.marketData.sell 
        : walletBuyItem.value.marketData.buy
        
    return Math.round(price * walletBuyAmount.value)
})

function openTradeModal(asset) {
    walletBuyItem.value = asset
    walletBuyAmount.value = 1
    
    if (asset.marketData?.buyActive) walletBuyType.value = 'buy'
    else if (asset.marketData?.sellActive) walletBuyType.value = 'sell'
    else walletBuyType.value = 'buy'
    
    walletBuyModal.value = true
}

function closeWalletBuy() {
    if (!isSubmitting.value) {
        walletBuyModal.value = false
        walletBuyItem.value = null
    }
}

async function submitWalletBuy() {
    if (!walletBuyItem.value) return
    if (walletBuyAmount.value <= 0) {
        Swal.fire({ icon: 'warning', title: 'خطا', text: 'مقدار وارد شده نامعتبر است', background: '#0f1517', color: '#fff' })
        return
    }
    
    if (walletBuyType.value === 'sell' && walletBuyAmount.value > walletBuyItem.value.balance) {
         Swal.fire({ icon: 'error', title: 'موجودی ناکافی', text: 'مقدار فروش نمی‌تواند بیشتر از موجودی شما باشد.', background: '#0f1517', color: '#fff' })
         return
    }

    isSubmitting.value = true
    try {
        const socketKey = getSocketKeyFromStandard(walletBuyItem.value.name)
        
        const payload = {
            item: socketKey,
            amount: Number(walletBuyAmount.value),
            type: walletBuyType.value 
        }

        const res = await axios.post(`${API_URL}/user/wallet/order`, payload, { withCredentials: true })
        
        closeWalletBuy()
        
        Swal.fire({
            icon: 'success',
            title: 'تراکنش موفق',
            text: res.data.msg || "سفارش شما با موفقیت ثبت شد.",
            confirmButtonColor: "#00c897",
            confirmButtonText: "بسیار عالی",
            background: "#0f1517",
            color: "#fff",
            timer: 3000,
            timerProgressBar: true
        })
        
        await fetchAssets()

    } catch (err) {
        const msg = err?.response?.data?.error || err?.response?.data?.message || "خطا در برقراری ارتباط با سرور"
        Swal.fire({
            icon: 'error',
            title: 'خطا در تراکنش',
            text: msg,
            confirmButtonColor: "#ff6b6b",
            confirmButtonText: "بررسی می‌کنم",
            background: "#0f1517",
            color: "#fff"
        })
    } finally {
        isSubmitting.value = false
    }
}
</script>

<style scoped>
.modal-enter-active, .modal-leave-active {
  transition: opacity 0.3s ease;
}
.modal-enter-from, .modal-leave-to {
  opacity: 0;
}
.modal-enter-active > div, .modal-leave-active > div {
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.modal-enter-from > div {
  transform: scale(0.9) translateY(20px);
  opacity: 0;
}
.modal-leave-to > div {
  transform: scale(0.95);
  opacity: 0;
}

input[type=number]::-webkit-inner-spin-button, 
input[type=number]::-webkit-outer-spin-button { 
  -webkit-appearance: none; 
  margin: 0; 
}
</style>